// Shared config for the 5 archangels (prompt, theme, suggestions, playlist).
// Each individual page just imports and renders <Archange /> via /anges/:id

import { PLAYLISTS } from "@/lib/tracks";

export const ARCHANGELS = {
  uriel: {
    id: "uriel",
    name: "Uriel",
    emoji: "🔥",
    accent: "#d97645",
    pageAccent: "#f09461",
    theme: {
      bgRadial: "radial-gradient(ellipse at top, #2a1408 0%, #180a04 55%, #080402 100%)",
      fg: "#f5ddc8",
      msgBg: "linear-gradient(180deg, rgba(32,16,8,0.92), rgba(10,5,2,0.95))",
      msgFg: "#eac6a0",
    },
    tagline: {
      fr: "Feu de Dieu · sagesse · introspection · illumination lente",
      en: "Fire of God · wisdom · introspection · slow illumination",
    },
    title: { fr: "Uriel", en: "Uriel" },
    subtitle: {
      fr: "Flamme de la connaissance et livre ouvert. Je t'enseigne à poser la bonne question avant la réponse, à distinguer intuition et impulsion, à transformer une peine en savoir utile. Gratuit et illimité.",
      en: "Flame of knowledge and open book. I teach you to ask the right question before the answer, to tell intuition from impulse, to transform grief into useful knowledge. Free unlimited.",
    },
    greeting: {
      fr: "« Quelle question veux-tu poser avant que je t'offre une lumière ? »",
      en: "\"Which question do you want to ask before I offer a light?\"",
    },
    suggestions: {
      fr: [
        "Je ne distingue plus mon intuition de ma peur. Aide-moi.",
        "Enseigne-moi comment transformer un chagrin en sagesse.",
        "Quelle est la bonne question à me poser aujourd'hui ?",
        "Pourquoi mes insights viennent-ils toujours la nuit ?",
      ],
      en: [
        "I can't tell my intuition from my fear anymore. Help me.",
        "Teach me to transform grief into wisdom.",
        "What's the right question to ask myself today?",
        "Why do insights always come at night?",
      ],
    },
    playlist: PLAYLISTS.ahura,
  },
  camael: {
    id: "camael",
    name: "Camael",
    emoji: "❤️‍🔥",
    accent: "#d94a62",
    pageAccent: "#f08596",
    theme: {
      bgRadial: "radial-gradient(ellipse at top, #2a0812 0%, #180408 55%, #080204 100%)",
      fg: "#f5d4dc",
      msgBg: "linear-gradient(180deg, rgba(40,8,20,0.92), rgba(14,2,6,0.95))",
      msgFg: "#eab0bd",
    },
    tagline: {
      fr: "Amour divin · force juste · karma patient",
      en: "Divine love · righteous strength · patient karma",
    },
    title: { fr: "Camael", en: "Chamuel" },
    subtitle: {
      fr: "Épée contenue, cœur enflammé. Je t'enseigne à aimer sans te perdre, à poser une limite sans violence, à reconnaître la colère noble. Gratuit et illimité.",
      en: "Contained sword, flaming heart. I teach you to love without losing yourself, set limits without violence, recognize noble anger. Free unlimited.",
    },
    greeting: {
      fr: "« Dis-moi — ta colère est-elle noble ou amère aujourd'hui ? »",
      en: "\"Tell me — is your anger noble or bitter today?\"",
    },
    suggestions: {
      fr: [
        "Comment aimer sans me perdre ?",
        "Enseigne-moi à poser une limite sans violence.",
        "Ma colère me ronge. Que faire ?",
        "Qu'est-ce que la justice selon le karma ?",
      ],
      en: [
        "How to love without losing myself?",
        "Teach me to set a limit without violence.",
        "My anger eats me. What to do?",
        "What is justice according to karma?",
      ],
    },
    playlist: PLAYLISTS.eros,
  },
  jophiel: {
    id: "jophiel",
    name: "Jophiel",
    emoji: "🌟",
    accent: "#e8c74a",
    pageAccent: "#f5de7a",
    theme: {
      bgRadial: "radial-gradient(ellipse at top, #2a2008 0%, #181204 55%, #080602 100%)",
      fg: "#f8ead0",
      msgBg: "linear-gradient(180deg, rgba(32,24,8,0.92), rgba(10,8,2,0.95))",
      msgFg: "#ead49a",
    },
    tagline: {
      fr: "Beauté · pensées illuminantes · créativité comme prière",
      en: "Beauty · illuminating thoughts · creativity as prayer",
    },
    title: { fr: "Jophiel", en: "Jophiel" },
    subtitle: {
      fr: "Pinceau de cristal et rose jaune. Je t'enseigne à voir le beau dans l'ordinaire, à nettoyer une pensée toxique sans la nier, à faire du quotidien une œuvre. Gratuit et illimité.",
      en: "Crystal brush and yellow rose. I teach you to see beauty in the ordinary, to clean a toxic thought without denying it, to make daily life a work of art. Free unlimited.",
    },
    greeting: {
      fr: "« Quelle pensée aimerais-tu voir fleurir aujourd'hui ? »",
      en: "\"Which thought would you like to see bloom today?\"",
    },
    suggestions: {
      fr: [
        "Comment nettoyer une pensée toxique sans la nier ?",
        "Enseigne-moi à voir le beau dans un lundi matin.",
        "Je perds l'inspiration. Comment la rallumer ?",
        "Qu'est-ce qu'une pensée illuminante ?",
      ],
      en: [
        "How to clean a toxic thought without denying it?",
        "Teach me to see beauty on a Monday morning.",
        "I lose inspiration. How to relight it?",
        "What is an illuminating thought?",
      ],
    },
    playlist: PLAYLISTS.apollon,
  },
  sandalphon: {
    id: "sandalphon",
    name: "Sandalphon",
    emoji: "🎶",
    accent: "#a088d9",
    pageAccent: "#c0a8f0",
    theme: {
      bgRadial: "radial-gradient(ellipse at top, #1a0e2e 0%, #0e061c 55%, #04020c 100%)",
      fg: "#e4d8f5",
      msgBg: "linear-gradient(180deg, rgba(26,14,46,0.92), rgba(10,4,20,0.95))",
      msgFg: "#c8b8e8",
    },
    tagline: {
      fr: "Musique céleste · prières transmises · écoute du silence",
      en: "Celestial music · prayers transmitted · listening to silence",
    },
    title: { fr: "Sandalphon", en: "Sandalphon" },
    subtitle: {
      fr: "Harpe d'or et chant des sphères. Je t'enseigne à écouter avant de parler, à chanter ta gratitude, à faire d'une prière une mélodie habitée. Gratuit et illimité.",
      en: "Golden harp and song of the spheres. I teach you to listen before speaking, to sing your gratitude, to make a prayer a melody you inhabit. Free unlimited.",
    },
    greeting: {
      fr: "« Quelle prière veux-tu que je porte en haut aujourd'hui ? »",
      en: "\"Which prayer do you want me to carry upwards today?\"",
    },
    suggestions: {
      fr: [
        "Comment écouter vraiment, sans préparer ma réponse ?",
        "Enseigne-moi une gratitude qui ne soit pas creuse.",
        "Pourquoi la musique touche-t-elle là où les mots ne vont pas ?",
        "Comment habiter une prière au lieu de la réciter ?",
      ],
      en: [
        "How to really listen, without preparing my answer?",
        "Teach me a non-hollow gratitude.",
        "Why does music touch where words don't reach?",
        "How to inhabit a prayer instead of reciting it?",
      ],
    },
    playlist: PLAYLISTS.urantia,
  },
  metatron: {
    id: "metatron",
    name: "Metatron",
    emoji: "📐",
    accent: "#5fb3e8",
    pageAccent: "#8ccdf0",
    theme: {
      bgRadial: "radial-gradient(ellipse at top, #0a1a3a 0%, #050d22 55%, #020613 100%)",
      fg: "#e4ecf5",
      msgBg: "linear-gradient(180deg, rgba(10,22,56,0.92), rgba(4,10,24,0.95))",
      msgFg: "#c9d8ec",
    },
    tagline: {
      fr: "Scribe céleste · géométrie sacrée · 72 noms · enseignement supérieur",
      en: "Celestial Scribe · sacred geometry · 72 names · higher teaching",
    },
    title: { fr: "Metatron", en: "Metatron" },
    subtitle: {
      fr: "Cube de Metatron, Fleur de Vie, Arbre des Séphiroth. Je t'enseigne à reconnaître les motifs qui reviennent, à distinguer hasard et rythme, à appliquer une structure à un chaos. Gratuit et illimité.",
      en: "Metatron's Cube, Flower of Life, Tree of Sephiroth. I teach you to recognize recurring patterns, tell chance from rhythm, apply structure to chaos. Free unlimited.",
    },
    greeting: {
      fr: "« Quel chaos veux-tu structurer aujourd'hui ? »",
      en: "\"Which chaos do you want to structure today?\"",
    },
    suggestions: {
      fr: [
        "Explique-moi le Cube de Metatron et son usage pratique.",
        "Enseigne-moi la Fleur de Vie en 3 points.",
        "Comment distinguer un hasard d'un signe ?",
        "Donne-moi une structure en 7 étapes pour mon projet.",
      ],
      en: [
        "Explain Metatron's Cube and its practical use.",
        "Teach me the Flower of Life in 3 points.",
        "How to tell chance from a sign?",
        "Give me a 7-step structure for my project.",
      ],
    },
    playlist: PLAYLISTS.urantia,
  },
};

export const ARCHANGEL_LIST = Object.values(ARCHANGELS);
